import { useState, useEffect } from 'react';

export default function Calculator() {
  const [propertyValue, setPropertyValue] = useState(200000);
  const [downPayment, setDownPayment] = useState(20000);
  const [months, setMonths] = useState(360);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [financedAmount, setFinancedAmount] = useState(0);

  useEffect(() => {
    const financed = propertyValue - downPayment;
    setFinancedAmount(financed);

    // Taxa de juros anual aproximada do MCMV (5% ao ano)
    const annualRate = 0.05;
    const monthlyRate = annualRate / 12;

    if (financed > 0 && months > 0) {
      const payment =
        (financed * monthlyRate * Math.pow(1 + monthlyRate, months)) /
        (Math.pow(1 + monthlyRate, months) - 1);
      setMonthlyPayment(payment);
    } else {
      setMonthlyPayment(0);
    }
  }, [propertyValue, downPayment, months]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  return (
    <section id="calculadora" className="py-20 bg-gradient-to-br from-teal-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Calcule Seu Financiamento
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Simule o valor das parcelas do seu imóvel e veja como é possível realizar o sonho da casa própria
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div>
                <label className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-700">Valor do Imóvel</span>
                  <span className="text-lg font-bold text-teal-600">{formatCurrency(propertyValue)}</span>
                </label>
                <input
                  type="range"
                  min="100000"
                  max="500000"
                  step="10000"
                  value={propertyValue}
                  onChange={(e) => setPropertyValue(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>R$ 100.000</span>
                  <span>R$ 500.000</span>
                </div>
              </div>

              <div>
                <label className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-700">Entrada</span>
                  <span className="text-lg font-bold text-teal-600">{formatCurrency(downPayment)}</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max={propertyValue * 0.5}
                  step="5000"
                  value={downPayment}
                  onChange={(e) => setDownPayment(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>R$ 0</span>
                  <span>{formatCurrency(propertyValue * 0.5)}</span>
                </div>
              </div>

              <div>
                <label className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-700">Prazo</span>
                  <span className="text-lg font-bold text-teal-600">{months} meses ({Math.floor(months / 12)} anos)</span>
                </label>
                <input
                  type="range"
                  min="120"
                  max="420"
                  step="12"
                  value={months}
                  onChange={(e) => setMonths(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>10 anos</span>
                  <span>35 anos</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-center">
              <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 text-white">
                <div className="mb-6">
                  <p className="text-sm font-medium text-teal-100 mb-2">Valor Financiado</p>
                  <p className="text-3xl font-bold">{formatCurrency(financedAmount)}</p>
                </div>

                <div className="border-t border-teal-500 pt-6 mb-6">
                  <p className="text-sm font-medium text-teal-100 mb-2">Parcela Mensal Estimada</p>
                  <p className="text-5xl font-bold mb-2">{formatCurrency(monthlyPayment)}</p>
                  <p className="text-sm text-teal-100">por {months} meses</p>
                </div>

                <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-information-line text-xl"></i>
                    </div>
                    <div>
                      <p className="text-xs text-teal-50">
                        <strong>Simulação exemplificativa</strong> com taxa de 5% ao ano. Os valores reais podem variar conforme análise de crédito e condições do programa Minha Casa Minha Vida.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={() => {
                  const element = document.getElementById('formulario');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="mt-6 w-full bg-gray-900 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-gray-800 transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                Solicitar Proposta Personalizada
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
